import { Injectable, inject } from '@angular/core';
import { BottomToastComponent, type BottomToastContext, type BottomToastMessageType } from './bottom-toast.component';
import { ToastService } from '../toast/toast.service';

export interface BottomToastOptions extends BottomToastContext {
    message: string;
    /** Semantic message type: normal (default), info, warning, error */
    type?: BottomToastMessageType;
}

@Injectable({
    providedIn: 'root',
})
export class BottomToastService {
    private readonly toastService = inject(ToastService);
    private _activeToastId: string | null = null;

    private createId(): string {
        return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    }

    public open(options: BottomToastOptions): string | number {
        const durationSeconds = options.durationSeconds ?? 5;
        // Replace any existing bottom toast (selection changes should update, not stack).
        if (this._activeToastId) {
            this.toastService.dismiss(this._activeToastId);
        }

        const toastId = this.createId();
        this._activeToastId = toastId;

        this.toastService.custom({
            id: toastId,
            component: BottomToastComponent,
            position: 'bottom-center',
            durationMs: durationSeconds * 1000,
            dismissible: false, // handled by component close button
            componentInputs: {
                messageInput: options.message,
                typeInput: options.type ?? 'normal',
                sizeInput: options.size ?? 4,
                variantInput: options.variant ?? 1,
                showCloseInput: options.showClose ?? true,
                actionLabelInput: options.actionLabel,
                actionInput: options.action,
                secondaryActionLabelInput: options.secondaryActionLabel,
                secondaryActionInput: options.secondaryAction,
                closeInput: () => {
                    this.toastService.dismiss(toastId);
                    if (this._activeToastId === toastId) {
                        this._activeToastId = null;
                    }
                },
            },
        });

        return toastId;
    }
}
